// ===========================================
// SERVICE: Documentos (CORE)
// ===========================================
/**
 * Servicio para gestion de documentos.
 *
 * IMPORTANTE: Todos los metodos reciben el cliente Supabase autenticado como primer parametro.
 * Usar con useSupabase() desde el hook que llame a estos metodos.
 */

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import { getStorageAdapter, BUCKET_DOCUMENTOS } from '@/lib/storage'
import type { Documento, DocumentoConCliente } from '@/types/database'
import type {
  FiltrosDocumentos,
  MetadatosSubida,
  DocumentoInsert,
  DocumentoUpdate,
  DocumentoVinculacion,
  DocumentoVinculacionInsert,
  TipoEntidadDocumento,
  RolDocumento,
  EstadisticasDocumentos,
  EstadisticasDetalladas,
  CategoriaStats,
} from '../types/documentos'

// MIME types permitidos por el bucket `documentos-clientes` (ver migración
// 20260117_create_documentos_clientes_bucket). El navegador a veces reporta un
// `file.type` vacío o `application/octet-stream` (quirk de Windows con la
// asociación de extensiones rota), lo que el whitelist del bucket rechaza con
// un 400 opaco. Resolvemos el contentType desde la extensión como fallback.
const EXT_TO_MIME: Record<string, string> = {
  pdf: 'application/pdf',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
}
const ALLOWED_MIME_TYPES = new Set(Object.values(EXT_TO_MIME))

export function resolveContentType(file: File): string | undefined {
  if (file.type && ALLOWED_MIME_TYPES.has(file.type)) return file.type
  const ext = file.name.split('.').pop()?.toLowerCase()
  return (ext && EXT_TO_MIME[ext]) || file.type || undefined
}

// Una URL firmada no reevalúa RLS hasta vencer. Todos los callers la abren o la cargan al instante.
export const DOCUMENTO_URL_TTL_SECONDS = 120

export const documentosService = {
  // ===========================================
  // CRUD de Documentos
  // ===========================================

  /**
   * Sube un archivo al Storage y registra los metadatos en la BD.
   * Sin `organizacionId` falla antes de subir el archivo. Un documento sin cliente se registra en
   * esa organización. Con cliente, la base le asigna la organización del cliente.
   */
  async subir(
    client: SupabaseClient<Database>,
    file: File,
    clienteId: string,
    createdBy: string,
    metadatos?: MetadatosSubida,
    organizacionId?: string | null
  ): Promise<Documento> {
    if (!organizacionId) {
      throw new Error('No hay una organización activa para subir el documento. Recargue la página.')
    }
    const storage = getStorageAdapter(client)
    const categoria = metadatos?.categoria || 'otros'
    const timestamp = Date.now()
    const nombreArchivo = `${timestamp}_${file.name.replace(/[^a-zA-Z0-9.-]/g, '_')}`
    // Sin cliente: cliente_id NULL y ruta general/<organización>/<categoría>/<archivo>. Las policies
    // del bucket deciden el acceso por la organización de esa ruta.
    const cid = clienteId || null
    const path = cid
      ? `clientes/${cid}/${categoria}/${nombreArchivo}`
      : `general/${organizacionId}/${categoria}/${nombreArchivo}`

    await storage.upload(BUCKET_DOCUMENTOS, path, file, {
      contentType: resolveContentType(file),
      upsert: false,
    })

    const documentoData: DocumentoInsert = {
      cliente_id: cid,
      nombre: metadatos?.nombre || file.name,
      nombre_original: file.name,
      categoria: categoria,
      descripcion: metadatos?.descripcion?.trim() || null,
      archivo_path: path,
      archivo_size: file.size,
      mime_type: file.type,
      fecha_vencimiento: metadatos?.fecha_vencimiento || null,
      created_by: createdBy,
      organizacion_id: organizacionId,
    }

    const { data, error: dbError } = await client
      .from('documentos')
      .insert(documentoData)
      .select()
      .single()

    if (dbError) {
      await storage.delete(BUCKET_DOCUMENTOS, [path])
      throw new Error(`Error al registrar documento: ${dbError.message}`)
    }

    if (cid) {
      await this.vincular(client, data.id, 'cliente', cid, metadatos?.workspace || 'contable', 'adjunto')
    }

    return data as Documento
  },

  /**
   * Lista todos los documentos con filtros opcionales
   */
  async listar(client: SupabaseClient<Database>, filtros?: FiltrosDocumentos): Promise<Documento[]> {
    let query = client
      .from('documentos')
      .select('*')
      .order('created_at', { ascending: false })

    if (filtros?.cliente_id) {
      query = query.eq('cliente_id', filtros.cliente_id)
    }
    if (filtros?.categoria) {
      query = query.eq('categoria', filtros.categoria)
    }
    if (filtros?.busqueda) {
      query = query.or(`nombre.ilike.%${filtros.busqueda}%,nombre_original.ilike.%${filtros.busqueda}%`)
    }

    const { data, error } = await query
    if (error) throw error
    return data as Documento[]
  },

  /**
   * Lista documentos de un cliente especifico
   */
  async listarPorCliente(client: SupabaseClient<Database>, clienteId: string, categoria?: string): Promise<Documento[]> {
    let query = client
      .from('documentos')
      .select('*')
      .eq('cliente_id', clienteId)
      .order('created_at', { ascending: false })

    if (categoria) {
      query = query.eq('categoria', categoria)
    }

    const { data, error } = await query
    if (error) throw error
    return data as Documento[]
  },

  /**
   * Obtiene un documento por su ID
   */
  async obtenerPorId(client: SupabaseClient<Database>, id: string): Promise<Documento> {
    const { data, error } = await client
      .from('documentos')
      .select('*')
      .eq('id', id)
      .single()

    if (error) throw error
    return data as Documento
  },

  /**
   * Genera una URL firmada para descargar/visualizar un archivo
   */
  async obtenerUrl(client: SupabaseClient<Database>, path: string): Promise<string> {
    const storage = getStorageAdapter(client)
    return storage.getSignedUrl(BUCKET_DOCUMENTOS, path, DOCUMENTO_URL_TTL_SECONDS)
  },

  /**
   * Descarga un archivo directamente
   */
  async descargar(client: SupabaseClient<Database>, path: string): Promise<Blob> {
    const storage = getStorageAdapter(client)
    return storage.download(BUCKET_DOCUMENTOS, path)
  },

  /**
   * Actualiza los metadatos de un documento
   */
  async actualizar(client: SupabaseClient<Database>, id: string, data: DocumentoUpdate): Promise<Documento> {
    const { data: documento, error } = await client
      .from('documentos')
      .update(data)
      .eq('id', id)
      .select()
      .single()

    if (error) throw error
    return documento as Documento
  },

  /**
   * Elimina un documento (archivo de Storage y registro de BD)
   */
  async eliminar(client: SupabaseClient<Database>, id: string): Promise<void> {
    const storage = getStorageAdapter(client)
    const documento = await this.obtenerPorId(client, id)

    // Eliminar archivo de storage (no protegido por RPC)
    try {
      await storage.delete(BUCKET_DOCUMENTOS, [documento.archivo_path])
    } catch (storageError) {
      console.warn(`Error al eliminar archivo de storage: ${storageError}`)
    }

    // RPC con RBAC: verifica rol del caller, elimina vinculaciones y registro
    const { error } = await client.rpc('eliminar_documento_seguro', { p_id: id })
    if (error) throw error
  },

  /**
   * Borra únicamente el archivo de Storage (sin tocar la fila `documentos` ni pasar por RBAC).
   * Usado por movimientosService.eliminar (ORR-218): al borrar un movimiento, la RPC
   * `eliminar_movimiento_seguro` elimina server-side la fila del comprobante (autorizada por
   * `eliminarMovimiento`, cubre todos los roles); SQL no accede al bucket, así que el archivo
   * se limpia acá con el `archivo_path` capturado antes del borrado.
   */
  async eliminarArchivo(client: SupabaseClient<Database>, archivoPath: string): Promise<void> {
    const storage = getStorageAdapter(client)
    await storage.delete(BUCKET_DOCUMENTOS, [archivoPath])
  },

  // ===========================================
  // Vinculaciones Polimorficas
  // ===========================================

  /**
   * Vincula un documento a una entidad
   */
  async vincular(
    client: SupabaseClient<Database>,
    documentoId: string,
    entidadTipo: TipoEntidadDocumento,
    entidadId: string,
    workspace?: string,
    rol?: RolDocumento
  ): Promise<DocumentoVinculacion> {
    const vinculacion: DocumentoVinculacionInsert = {
      documento_id: documentoId,
      entidad_tipo: entidadTipo,
      entidad_id: entidadId,
      workspace: workspace || null,
      rol: rol || 'adjunto',
    }

    const { data, error } = await client
      .from('documento_vinculaciones')
      .insert(vinculacion)
      .select()
      .single()

    if (error) throw error
    return data as DocumentoVinculacion
  },

  /**
   * Desvincula un documento de una entidad
   */
  async desvincular(client: SupabaseClient<Database>, documentoId: string, entidadTipo: TipoEntidadDocumento, entidadId: string): Promise<void> {
    const { error } = await client
      .from('documento_vinculaciones')
      .delete()
      .eq('documento_id', documentoId)
      .eq('entidad_tipo', entidadTipo)
      .eq('entidad_id', entidadId)

    if (error) throw error
  },

  /**
   * Obtiene documentos vinculados a una entidad
   */
  async obtenerPorEntidad(
    client: SupabaseClient<Database>,
    entidadTipo: TipoEntidadDocumento,
    entidadId: string,
    rol?: RolDocumento
  ): Promise<Documento[]> {
    let query = client
      .from('documento_vinculaciones')
      .select(`documento:documentos(*)`)
      .eq('entidad_tipo', entidadTipo)
      .eq('entidad_id', entidadId)

    if (rol) {
      query = query.eq('rol', rol)
    }

    const { data, error } = await query
    if (error) throw error

    return (data || [])
      .map((v: { documento: Documento | null }) => v.documento)
      .filter((d): d is Documento => d !== null)
  },

  /**
   * Obtiene las vinculaciones de un documento
   */
  async obtenerVinculaciones(client: SupabaseClient<Database>, documentoId: string): Promise<DocumentoVinculacion[]> {
    const { data, error } = await client
      .from('documento_vinculaciones')
      .select('*')
      .eq('documento_id', documentoId)

    if (error) throw error
    return data as DocumentoVinculacion[]
  },

  // ===========================================
  // Consultas especializadas
  // ===========================================

  /**
   * Obtiene documentos proximos a vencer
   */
  async obtenerPorVencer(client: SupabaseClient<Database>, diasLimite: number = 30): Promise<DocumentoConCliente[]> {
    const { data, error } = await client
      .from('vista_documentos_por_vencer')
      .select('*')
      .lte('dias_restantes', diasLimite)
      .order('dias_restantes', { ascending: true })

    if (error) throw error
    return (data ?? []) as DocumentoConCliente[]
  },

  /**
   * Busca documentos por nombre
   */
  async buscar(client: SupabaseClient<Database>, query: string): Promise<DocumentoConCliente[]> {
    const { data, error } = await client
      .from('documentos')
      .select(`
        *,
        cliente:clientes!cliente_id (
          id,
          persona:personas(nombre_completo, cuit)
        )
      `)
      .or(`nombre.ilike.%${query}%,nombre_original.ilike.%${query}%`)
      .order('created_at', { ascending: false })
      .limit(50)

    if (error) throw error

    type DocWithCliente = Documento & {
      cliente: { id: string; persona: { nombre_completo: string | null; cuit: number | null } | null } | null
    }
    const docs = (data ?? []) as DocWithCliente[]
    return docs.map((doc) => ({
      ...doc,
      cliente_nombre: doc.cliente?.persona?.nombre_completo ?? null,
      cliente_cuit: doc.cliente?.persona?.cuit ?? null,
      dias_restantes: null,
    })) as DocumentoConCliente[]
  },

  /**
   * Obtiene estadisticas de documentos de un cliente
   */
  async obtenerEstadisticas(client: SupabaseClient<Database>, clienteId: string): Promise<EstadisticasDocumentos> {
    const { data, error } = await client
      .from('documentos')
      .select('categoria, archivo_size')
      .eq('cliente_id', clienteId)

    if (error) throw error

    const documentos = data || []
    const porCategoria: Record<string, number> = {}
    let tamanioTotal = 0

    documentos.forEach((doc: { categoria: string | null; archivo_size: number | null }) => {
      const cat = doc.categoria || 'otros'
      porCategoria[cat] = (porCategoria[cat] || 0) + 1
      tamanioTotal += doc.archivo_size || 0
    })

    return {
      total: documentos.length,
      porCategoria,
      tamanioTotal,
    }
  },

  /**
   * Obtiene estadisticas detalladas por categoria (cantidad + tamano)
   */
  async obtenerEstadisticasDetalladas(client: SupabaseClient<Database>, clienteId: string): Promise<EstadisticasDetalladas> {
    const { data, error } = await client
      .from('documentos')
      .select('categoria, archivo_size')
      .eq('cliente_id', clienteId)

    if (error) throw error

    const documentos = data || []
    const statsMap = new Map<string, { cantidad: number; tamano: number }>()
    let tamanioTotal = 0

    documentos.forEach((doc: { categoria: string | null; archivo_size: number | null }) => {
      const cat = doc.categoria || 'otros'
      const size = doc.archivo_size || 0
      const current = statsMap.get(cat) || { cantidad: 0, tamano: 0 }
      statsMap.set(cat, {
        cantidad: current.cantidad + 1,
        tamano: current.tamano + size,
      })
      tamanioTotal += size
    })

    const categorias: CategoriaStats[] = Array.from(statsMap.entries()).map(([cat, stats]) => ({
      categoria: cat,
      cantidad: stats.cantidad,
      tamanoTotal: stats.tamano,
    }))

    return {
      total: documentos.length,
      tamanioTotal,
      categorias,
    }
  },
}
